import { useState, useContext, useEffect, } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Button, Avatar, Typography, Divider, Dropdown, Card, Flex, Drawer } from 'antd';
import { MenuOutlined, UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { AdvancedSearch } from './AdvancedSearch/AdvancedSearch.component';
import { useApp } from '../../../context/app-context';
import { PermissionsContext } from '../../../router/DashboardRoutes';
import { isCustomer } from '../../../UserValidation';
import AvaLAN_Logo from '../../../../src/assets/AvaLAN_Logo.png';
import './NavBar.css';

const { Text } = Typography;

const truncateTextStyle = {
    whiteSpace: 'nowrap', 
    overflow: 'hidden', 
    textOverflow: 'ellipsis', 
    maxWidth: 250
};

const profileDropdownStyle = {
    zIndex: 2000, 
    backgroundColor: 'white',
    position: 'fixed',  
    right: 20,
    top: 65,
    minWidth: 300,
    maxWidth: 360,
    boxShadow: 'var(--navbar-box-shadow)'
}

export const ConditionalLink = ({ children, condition, ...props}) => {

    return (
        <>
            { condition ? 
                <Typography.Link >{children}</Typography.Link> 
            : 
                <Link {...props}>{children}</Link>
            }
        </>
    )
}

ConditionalLink.defaultProps = {
    condition: false,
}

export const NavBar = ({pendingState, pendingChangesModal, setPendingChangesModal, ...props}) => {

    const {state: {user}} = useApp();
    const { pathname } = useLocation();
    const userPermissions = useContext(PermissionsContext);

    const [isAdvancedSearchVisible, setIsAdvancedSearchVisible] = useState(true);
    const [profileDropdownVisible, setProfileDropdownVisible] = useState(false);
    const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);

    useEffect(() => {
        const showAdvancedSearch = pathname === "/app" || pathname.includes("station")
        window.scrollTo(0, 0);

        if(showAdvancedSearch){
            setIsAdvancedSearchVisible(true);
        } else {
            setIsAdvancedSearchVisible(false);
        }
    }, [pathname])

    const menuItems = [
        { 
            label: (<ConditionalLink to='' condition={pendingState}>{isCustomer(user) ? 'Locations' : 'NOC Dashboard'}</ConditionalLink>), 
            key: '/app'
        },
        { 
            label: (<ConditionalLink to='update-manager' condition={pendingState}>Update Manager</ConditionalLink>), 
            key: '/app/update-manager',
            hidden: (!userPermissions.access.updateManager)
        },
        { 
            label: (<ConditionalLink to='access-management' condition={pendingState} >Access Management</ConditionalLink>), 
            key: '/app/access-management',
            hidden: (!userPermissions.access.accessManagement)
        },
    ];

    const pendingStateHandler = (e) => {
        if(pendingState) {
            setPendingChangesModal(true);
        } else {
            setPendingChangesModal(false)
        }
        return 
    }

    const clearHandler = () => {
        if(pendingState) {
            setPendingChangesModal(true);
        } else {
            setPendingChangesModal(false)
            setIsAdvancedSearchVisible(true);
        }
        return 
    }

    const profileInfoEl = (
        <Flex gap='small' align='center'>
            <Avatar 
                size='large' 
                icon={!user?.firstName && <UserOutlined/>}
                style={{backgroundColor: 'var(--primary-color)'}}
                >
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
            </Avatar>
            <Flex vertical>
                {(user?.firstName || user?.lastName) &&
                    <Text style={truncateTextStyle} strong>{user?.firstName} {user?.lastName}</Text>
                }
                <Text style={truncateTextStyle}>{user?.email}</Text>
            </Flex>
        </Flex>
    )

    const profileLinksEl = (
        <>
            <span onClick={(e) => {
                pendingStateHandler(e); 
                setProfileDropdownVisible(false)
            }}>
                <ConditionalLink condition={pendingState} to='account'>
                    <UserOutlined style={{color: 'var(--primary-color)'}}/> View Account
                </ConditionalLink>
            </span>
            <span onClick={(e) => {
                pendingStateHandler(e); 
                setProfileDropdownVisible(false)
            }}>
                <ConditionalLink condition={pendingState} to='' onClick={() => pendingState ? undefined : window.location.reload()}>
                    <LogoutOutlined style={{color: 'var(--primary-color)'}}/> Sign Out
                </ConditionalLink>
            </span>
        </>
    )

    const accountContent = (
        <>
            <Card variant='outlined'>
                {profileInfoEl}
                <Divider style={{margin: '10px 0', color: '#000000'}}/>
                <Flex vertical gap='small'>
                    {profileLinksEl}
                </Flex>
            </Card>
        </>
        
    )

    const handleVisibleChange = (flag) => {
        setProfileDropdownVisible(flag);
    }

    return (
        <>
            <Flex 
                justify='space-between'
                align='center'
                id='navbar'
                className='navbar__container'
                wrap
                >
                {/* Logo + Menu */}
                <Flex gap='middle' align='center'>
                    <div className="navbar__logo" onClick={() => clearHandler()}>
                        {(!pendingState) 
                        ? <Link to=''><img src='/img/logoL' alt="AvaLAN Networks logo" style={{pointerEvents: 'none', }}/></Link>
                        : <span> <img src='/img/logoL' alt="AvaLAN Networks logo" onClick={ () => setPendingChangesModal(true) } style={{pointerEvents: 'none', }}/></span>}
                    </div>
                    <div className='navbar__desktop-only' >
                        <Menu
                            className="navbar__menu--links" 
                            onClick={(e) => pendingStateHandler(e)}
                            selectedKeys={[ pathname ]} 
                            mode="horizontal"
                            // overflowedIndicator={collapseMenu} 
                            items={menuItems}
                        />
                    </div>
                </Flex>
                {/* Advanced search (conditional) + Profile */}
                <Flex gap='middle' align='center' style={{padding: "8px 0"}}>
                    { userPermissions.access.advancedSearch && isAdvancedSearchVisible && 
                        <AdvancedSearch type='primary' ghost pendingState={pendingState} /> 
                    }
                    <Button className='navbar__mobile-only' type='primary' icon={<MenuOutlined/>} onClick={() => setMenuDrawerOpen(!menuDrawerOpen)}/>
                    
                    <div className='navbar__desktop-only'>
                        <Dropdown 
                            popupRender={() => accountContent}
                            trigger={['click']}
                            onOpenChange ={handleVisibleChange}
                            open={profileDropdownVisible}
                            overlayStyle={profileDropdownStyle}
                            >
                            <Avatar 
                                style={{backgroundColor: 'var(--primary-color)'}} 
                                icon={!user?.firstName && <UserOutlined/>}
                                >
                                {user?.firstName?.charAt(0) || ''}{user?.lastName?.charAt(0) || ''}
                            </Avatar>
                        </Dropdown>
                    </div>
                </Flex>
            </Flex>
            <Drawer 
                open={menuDrawerOpen}
                onClose={() => setMenuDrawerOpen(false)}
                >
                <Flex vertical justify='space-between' style={{height: '100%'}}>
                    <Flex vertical gap='large'>
                        {profileInfoEl}
                        <Menu
                            className='navbar__vertical-menu'
                            onClick={(e) => pendingStateHandler(e)}
                            selectedKeys={[ pathname ]} 
                            mode="vertical"
                            items={menuItems}
                            style={{border: 0}}
                        />
                    </Flex>
                    <Flex vertical gap='large'>
                        {profileLinksEl}
                    </Flex>
                </Flex>
            </Drawer>
        </>
    );
}